/**
 * The "why did this call fail" strip in the call pane.
 *
 * A failed call used to render as a zero-second row with a red pill and nothing
 * else, which reads as "the CRM is broken" even when the real cause is an
 * account-level Twilio switch nobody can see from here. Twilio's own reason now
 * travels with the row (`failureCode` / `failureText`); this turns the code into
 * a sentence the operator can act on, and falls back to Twilio's English when
 * the code is one nobody has written an explanation for yet.
 */
import { AlertTriangle } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import type { CallRow } from "../../../lib/calls";

/**
 * Twilio error codes we have written an explanation for. A code that is not in
 * here is not an error - it means the operator reads Twilio's own wording,
 * which is always shown underneath either way.
 */
const EXPLAINED = new Set([13227, 21215, 13224, 13223, 21210, 21212, 11200]);

/** The message key for a code, or null when only Twilio's own text applies. */
export function failureHintKey(code: number | null | undefined): string | null {
  if (code == null) return null;
  return EXPLAINED.has(code) ? `calls.failure.hint.${code}` : null;
}

/** Statuses that mean the call did not happen for a reason worth explaining. */
export function isFailedCall(status: string): boolean {
  return status === "failed" || status === "canceled";
}

export function CallFailure({ call }: { call: Pick<CallRow, "status" | "failureCode" | "failureText"> }) {
  const t = useT();
  if (!isFailedCall(call.status)) return null;

  const hint = failureHintKey(call.failureCode);
  const raw = call.failureText?.trim() ?? "";
  // Nothing fetched yet: the worker asks Twilio within a minute, and saying so
  // beats an empty strip that looks like the answer is "no reason".
  const pending = !hint && !raw;

  return (
    <div className="crm-call-fail flex flex-none gap-2.5 border-b border-[var(--crm-hair)] px-6 py-4 text-[12.75px]">
      <AlertTriangle className="crm-call-fail-mark mt-[1px] size-4 flex-none" />
      <span className="min-w-0">
        <span className="block font-semibold">
          {t("calls.failure.title")}
          {call.failureCode != null ? (
            <span className="ml-1.5 font-normal text-muted-foreground tabular-nums">
              {t("calls.failure.code", { code: String(call.failureCode) })}
            </span>
          ) : null}
        </span>
        {pending ? (
          <span className="mt-[3px] block text-muted-foreground">{t("calls.failure.pending")}</span>
        ) : (
          <>
            {hint ? <span className="mt-[3px] block">{t(hint)}</span> : null}
            {raw ? (
              <span className={`block text-muted-foreground ${hint ? "mt-[3px] text-[11.75px]" : "mt-[3px]"}`}>
                {raw}
              </span>
            ) : null}
          </>
        )}
      </span>
    </div>
  );
}
